import React, { useState } from "react";
import { X, Plus, Pencil, Trash2 } from "lucide-react";
import NahratObrazek from "./NahratObrazek";
import { Button, Field, Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";
import { useAres, IcoAres, AresProuzek } from "./AresPole";

const PRAZDNA_OSOBA = { jmeno: "", prijmeni: "", pozice: "", telefon: "", email: "" };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Kontaktní osoba partnera — pole sdílí formulář partnera i dialog osoby. */
export function OsobaPole({ o, setO, testid = "osoba" }) {
  const upd = (k) => (e) => setO({ ...o, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Jméno"><input className="field" value={o.jmeno} onChange={upd("jmeno")} data-testid={`${testid}-jmeno`} /></Field>
        <Field label="Příjmení"><input className="field" value={o.prijmeni} onChange={upd("prijmeni")} data-testid={`${testid}-prijmeni`} /></Field>
      </div>
      <Field label="Pozice"><input className="field" value={o.pozice} onChange={upd("pozice")} placeholder="např. architekt, jednatel" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Telefon"><input className="field amount" type="tel" value={o.telefon} onChange={upd("telefon")} data-testid={`${testid}-telefon`} /></Field>
        <Field label="E-mail"><input className="field" type="email" value={o.email} onChange={upd("email")} data-testid={`${testid}-email`} /></Field>
      </div>
    </div>
  );
}

export function chybaOsoby(o) {
  if (!o.prijmeni.trim()) return "Kontaktní osoba musí mít příjmení.";
  if (o.email && !EMAIL.test(o.email.trim())) return "Neplatný e-mail kontaktní osoby.";
  if (!o.telefon.trim() && !o.email.trim()) return "Vyplň u kontaktní osoby telefon nebo e-mail.";
  return "";
}

export function Sheet({ titulek, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[520px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="t-sekce font-800 text-ink">{titulek}</h2>
          <button onClick={onClose} className="tap md:min-h-0 text-muted hover:text-ink flex items-center justify-center" aria-label="Zavřít"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Úprava kontaktních osob přímo ve formuláři partnera — změny se uloží
 *  spolu s partnerem tlačítkem Uložit. */
function OsobyPartnera({ osoby, setOsoby, setError, onUpravuje }) {
  const [otevrena, setOtevrenaStav] = useState(null);     // index upravované osoby
  const setOtevrena = (i) => { setOtevrenaStav(i); onUpravuje(i !== null); };
  const [rozprac, setRozprac] = useState(null);
  const otevri = (i) => { setError(""); setOtevrena(i); setRozprac({ ...PRAZDNA_OSOBA, ...osoby[i] }); };
  const hotovo = () => {
    const ch = chybaOsoby(rozprac);
    if (ch) return setError(ch);
    setError("");
    setOsoby(osoby.map((o, i) => (i === otevrena ? rozprac : o)));
    setOtevrena(null);
  };
  const zrusit = () => {
    if (!osoby[otevrena]?.id && !Object.values(osoby[otevrena] || {}).some((v) => (v || "").trim()))
      setOsoby(osoby.filter((_, i) => i !== otevrena));          // nová prázdná osoba zmizí
    setOtevrena(null);
  };
  return (
    <div data-testid="partner-osoby">
      <div className="flex items-center mb-1">
        <span className="label-up text-muted">Kontaktní osoby</span>
        {otevrena === null && (
          <button type="button" data-testid="partner-osoba-pridat"
            onClick={() => { setOsoby([...osoby, { ...PRAZDNA_OSOBA }]); setOtevrena(osoby.length); setRozprac({ ...PRAZDNA_OSOBA }); }}
            className="tap md:min-h-0 ml-auto inline-flex items-center gap-1 t-meta font-700 text-accent">
            <Plus size={14} strokeWidth={2.4} /> Přidat
          </button>
        )}
      </div>
      {osoby.length === 0 && <p className="t-meta text-muted py-2">Zatím bez kontaktní osoby.</p>}
      {osoby.map((o, i) => (otevrena === i ? (
        <div key={o.id || `n${i}`} className="rounded-card bg-[#faf9f7] border border-line-softer p-4 my-2 space-y-3 rise">
          <OsobaPole o={rozprac} setO={setRozprac} testid="partner-osoba-uprava" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={zrusit} className="btn-ghost tap">Zrušit</button>
            <button type="button" onClick={hotovo} className="btn-primary tap" data-testid="partner-osoba-hotovo">Hotovo</button>
          </div>
        </div>
      ) : (
        <div key={o.id || `n${i}`} className="flex items-center gap-3 py-2.5 border-t border-line-soft" data-testid={`partner-osoba-radek-${i}`}>
          <span className="flex-1 min-w-0">
            <span className="block t-telo font-600 text-ink truncate">
              {[o.jmeno, o.prijmeni].filter(Boolean).join(" ")}
              {i === 0 && <span className="ml-2 label-up px-2 py-0.5 rounded-pill bg-[#f2f2ef] text-sec-3 align-middle">Hlavní</span>}
            </span>
            <span className="block t-meta text-muted truncate">{[o.pozice, o.telefon, o.email].filter(Boolean).join(" · ") || "bez kontaktu"}</span>
          </span>
          {otevrena === null && (
            <>
              <button type="button" onClick={() => otevri(i)} aria-label="Upravit osobu" data-testid={`partner-osoba-upravit-${i}`}
                className="tap md:min-h-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-muted hover:text-ink hover:bg-surface"><Pencil size={15} /></button>
              <button type="button" onClick={() => setOsoby(osoby.filter((_, j) => j !== i))} aria-label="Odebrat osobu"
                data-testid={`partner-osoba-odebrat-${i}`}
                className="tap md:min-h-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-muted hover:text-danger hover:bg-surface"><Trash2 size={15} /></button>
            </>
          )}
        </div>
      )))}
    </div>
  );
}

/** Založení / úprava obchodního partnera: logo, firemní údaje a kontaktní
 *  osoby (při založení jedna nepovinná, při úpravě celý seznam). */
export default function PartnerForm({ partner = null, onClose, onSaved }) {
  const uprava = !!partner;
  const [f, setF] = useState({
    nazev: partner?.nazev || "", ico: partner?.ico || "", dic: partner?.dic || "",
    adresa: { ulice: "", psc: "", mesto: "", ...(partner?.adresa || {}) },
    web: partner?.web || "", poznamky: partner?.poznamky || "", logo_url: partner?.logo_url || "",
  });
  const puvodniOsoby = partner?.kontaktni_osoby || [];
  const [osoby, setOsoby] = useState(puvodniOsoby);
  const [upravujeOsobu, setUpravujeOsobu] = useState(false);
  const [osoba, setOsoba] = useState(PRAZDNA_OSOBA);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const updA = (k) => (e) => setF({ ...f, adresa: { ...f.adresa, [k]: e.target.value } });
  // IČO → ARES doplní název, DIČ a sídlo (Zrušit vrátí formulář, jak byl).
  const ares = useAres();
  const zAres = (a) => {
    ares.prevzit(a, { ...f, ico: a.ico });
    setF((x) => ({ ...x, nazev: a.nazev, ico: a.ico, dic: a.dic || x.dic, adresa: { ...x.adresa, ...a.adresa } }));
  };
  const sv = ares.firma ? ares.svit : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nazev.trim()) return setError("Zadej název partnera.");
    if (upravujeOsobu) return setError("Nejdřív dokonči úpravu kontaktní osoby (Hotovo).");
    // Kontaktní osoba je nepovinná — kontroluje se jen, když něco vyplníš.
    const sOsobou = !uprava && Object.values(osoba).some((v) => (v || "").trim());
    if (sOsobou) { const ch = chybaOsoby(osoba); if (ch) return setError(ch); }
    setError(""); setSaving(true);
    try {
      let { data } = uprava
        ? await api.patch(`/api/partneri/${partner.id}`, f)
        : await api.post("/api/partneri", { ...f, kontaktni_osoby: sOsobou ? [osoba] : [] });
      if (uprava) {
        // Kontaktní osoby: smazané, změněné a nové (každá zvlášť, ať se nic nepřepíše naslepo).
        const pole = (o) => ({ jmeno: o.jmeno || "", prijmeni: o.prijmeni || "", pozice: o.pozice || "",
          telefon: o.telefon || "", email: o.email || "" });
        const zustava = new Set(osoby.filter((o) => o.id).map((o) => o.id));
        for (const o of puvodniOsoby) if (!zustava.has(o.id)) await api.delete(`/api/partneri/${partner.id}/osoby/${o.id}`);
        for (const o of osoby) {
          if (!o.id) await api.post(`/api/partneri/${partner.id}/osoby`, pole(o));
          else {
            const pred = puvodniOsoby.find((x) => x.id === o.id);
            if (JSON.stringify(pole(pred)) !== JSON.stringify(pole(o))) await api.patch(`/api/partneri/${partner.id}/osoby/${o.id}`, pole(o));
          }
        }
        data = (await api.get(`/api/partneri/${partner.id}`)).data;
      }
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Sheet titulek={uprava ? "Upravit partnera" : "Nový obchodní partner"} onClose={onClose} testid="partner-form">
      <form onSubmit={submit} className="p-6 space-y-4">
        <div>
          <span className="label-up text-muted block mb-1.5">Logo <span className="normal-case tracking-normal font-500">· nepovinné</span></span>
          <NahratObrazek tvar="znak" value={f.logo_url} testid="partner-logo"
            onChange={(v) => setF((x) => ({ ...x, logo_url: v || "" }))} />
        </div>
        <AresProuzek firma={ares.firma} onZrusit={() => setF(ares.zrusit())} />
        <Field label="Název firmy">
          <input className={`field ${sv}`} value={f.nazev} onChange={upd("nazev")} placeholder="např. G-up Delta, s.r.o." data-testid="partner-nazev" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="IČO"><IcoAres value={f.ico} onChange={(v) => setF((x) => ({ ...x, ico: v }))} onNalezeno={zAres} className={sv} testid="partner-ico" /></Field>
          <Field label="DIČ"><input className={`field amount ${sv}`} value={f.dic} onChange={upd("dic")} /></Field>
        </div>
        <div>
          <span className="label-up text-muted block mb-1.5">Sídlo</span>
          <div className="space-y-2">
            <input className={`field ${sv}`} value={f.adresa.ulice} onChange={updA("ulice")} placeholder="Ulice a č.p." aria-label="Ulice a č.p." />
            <div className="grid grid-cols-[110px_1fr] gap-2">
              <input className={`field amount ${sv}`} inputMode="numeric" value={f.adresa.psc} onChange={updA("psc")} placeholder="PSČ" aria-label="PSČ" />
              <input className={`field ${sv}`} value={f.adresa.mesto} onChange={updA("mesto")} placeholder="Město" aria-label="Město" data-testid="partner-mesto" />
            </div>
          </div>
        </div>
        <Field label="Web"><input className="field" value={f.web} onChange={upd("web")} placeholder="www.…" /></Field>

        {uprava && <OsobyPartnera osoby={osoby} setOsoby={setOsoby} setError={setError} onUpravuje={setUpravujeOsobu} />}
        {!uprava && (
          <div className="rounded-card border border-line-softer bg-[#faf9f7] p-4">
            <div className="t-telo font-700 text-ink">Kontaktní osoba <span className="t-meta font-500 text-muted">· nepovinné</span></div>
            <p className="t-meta text-muted mb-3">S kým u partnera jednáš. Klidně ji přidáš později na kartě partnera.</p>
            <OsobaPole o={osoba} setO={setOsoba} testid="partner-osoba" />
          </div>
        )}

        <Field label="Poznámky">
          <textarea className="field h-20 py-2 resize-none" value={f.poznamky} onChange={upd("poznamky")}
            placeholder="např. domluvená provize, typ zakázek" />
        </Field>
        {error && <div className="t-meta text-danger">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost">Zrušit</button>
          <Button disabled={saving} data-testid="partner-save">{saving ? <Spinner /> : uprava ? "Uložit" : "Založit partnera"}</Button>
        </div>
      </form>
    </Sheet>
  );
}
